import {
  Globe,
  Mail,
  MessageSquare,
  Mic,
  Phone,
  ShoppingBag,
  ShoppingCart,
  Star,
  Store,
  TestTube2,
} from 'lucide-react';
import {
  siFacebook,
  siGmail,
  siSalla,
  siShopify,
  siWoocommerce,
  siGoogle,
  siInstagram,
  siLinkedin,
  siMeta,
  siSnapchat,
  siTiktok,
  siWhatsapp,
  siX,
  siYoutube,
  type SimpleIcon,
} from 'simple-icons';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Channel marks. Brand channels use the brand's own logo (Simple Icons, CC0 path data) in white on the brand
 * colour, the way they appear on a phone's home screen, to identify where a conversation came from. Channels that
 * are not brands (email, SMS, voice, website chat, walk-in) use plain glyphs.
 */
const BRANDS: Record<string, { icon: SimpleIcon; background: string; fill?: string }> = {
  whatsapp: { icon: siWhatsapp, background: '#25D366' },
  instagram: {
    icon: siInstagram,
    background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
  },
  facebook: { icon: siFacebook, background: '#0866FF' },
  tiktok: { icon: siTiktok, background: '#000000' },
  // Snapchat's ghost is drawn in black on its yellow, as in Snap's brand guidelines.
  snapchat: { icon: siSnapchat, background: '#FFFC00', fill: '#000000' },
  x: { icon: siX, background: '#000000' },
  linkedin: { icon: siLinkedin, background: '#0A66C2' },
  youtube: { icon: siYoutube, background: '#FF0000' },
  youtube_channel: { icon: siYoutube, background: '#FF0000' },
  // Google's mark is shown in colour on white, as in its own guidelines.
  reviews: { icon: siGoogle, background: '#ffffff', fill: '#4285F4' },
  // Ad platforms (campaigns), shown like the channels.
  google: { icon: siGoogle, background: '#ffffff', fill: '#4285F4' },
  meta: { icon: siMeta, background: '#ffffff', fill: '#0866FF' },
  // Online stores and mailboxes (Integrations).
  shopify: { icon: siShopify, background: '#7AB55C' },
  woocommerce: { icon: siWoocommerce, background: '#7F54B3' },
  salla: { icon: siSalla, background: '#004956', fill: '#BAF3E6' },
  gmail: { icon: siGmail, background: '#ffffff', fill: '#EA4335' },
  google_business: { icon: siGoogle, background: '#ffffff', fill: '#4285F4' },
};

const GLYPHS: Record<string, ComponentType<{ size?: number }>> = {
  email: Mail,
  voice: Phone,
  sms: MessageSquare,
  webchat: Globe,
  walkin: Store,
  store: Store,
  online: ShoppingCart,
  zid: ShoppingBag,
  microsoft365: Mail,
  phone: Phone,
  simulator: TestTube2,
  reviews: Star,
};

/** Every channel LY360 handles, in display order (used for overviews and filters). */
export const ALL_CHANNELS = [
  'whatsapp',
  'instagram',
  'facebook',
  'tiktok',
  'snapchat',
  'email',
  'webchat',
  'sms',
  'voice',
  'x',
  'linkedin',
  'youtube',
  'reviews',
] as const;

export function ChannelIcon({ channel, size = 13 }: { channel: string; size?: number }) {
  const brand = BRANDS[channel];
  if (brand) {
    return (
      <i className="ch-mark is-brand" style={{ background: brand.background }} aria-hidden="true">
        <svg viewBox="0 0 24 24" width={size} height={size} fill={brand.fill ?? '#fff'} focusable="false">
          <path d={brand.icon.path} />
        </svg>
      </i>
    );
  }
  const Icon = GLYPHS[channel] ?? Mic;
  return (
    <i
      className="ch-mark"
      style={{ background: `var(--ch-${channel}, var(--ch-simulator))` }}
      aria-hidden="true"
    >
      <Icon size={size} />
    </i>
  );
}

export function ChannelBadge({ channel, showLabel = true }: { channel: string; showLabel?: boolean }) {
  const { t } = useTranslation();
  const label = t(`channels.${channel}`, { defaultValue: channel });
  return (
    <span className="channel-badge" title={label}>
      <ChannelIcon channel={channel} />
      {showLabel ? <span>{label}</span> : <span className="sr-only">{label}</span>}
    </span>
  );
}
